import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import {
  apiV2EnergyOverviewGetAlarmLevelCountPost,
  V2EnergyOverviewGetAlarmLevelCountPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';
import { AlarmLevel, AlarmLevelColorDisplay } from '../../../../const';
import { Spin } from 'antd';
import SubTitle from '@/pages/WgqAllMaps/components/SubTitle';
import { getPieChartOption } from './chart';
import { ShareDataUnityEnergyCockpitMapContext } from '../../../..';
import { getDefaultTime } from '@/pages/WgqAllMaps/utils';
import titlePng from '@/assets/images/title_icon.png';
import Marquee from '../AlarmType/marquee';
import { keepOneOrLine } from '@/utils/utils';

export interface PieLegendProp {
  name: string;
  level: AlarmLevel;
  color: string;
  data?: V2EnergyOverviewGetAlarmLevelCountPostResponse['highLevel'];
}

const AlarmStatistics = () => {
  const theme = useTheme();
  const { timeResolution, dateValue } = useContext(ShareDataUnityEnergyCockpitMapContext);

  const [pieLegendData, setPieLegendData] = useState<PieLegendProp[]>();
  const chartRef = useRef(null);

  // usePieAutoLoop(chartRef, {
  //   length: pieLegendData?.length ?? 0,
  // });

  // 统计等级统计
  const { data: alarmLevelStatistics, loading } = useRequest(
    async () => {
      return await apiV2EnergyOverviewGetAlarmLevelCountPost({
        timeResolution,
        fromTime: getDefaultTime(timeResolution!, dateValue)?.startTime,
        toTime: getDefaultTime(timeResolution!, dateValue)?.endTime,
      });
    },
    {
      refreshDeps: [timeResolution, dateValue],
      ready: !!timeResolution && !!dateValue,
    }
  );

  useEffect(() => {
    setPieLegendData(
      [
        {
          name: '最高级',
          level: AlarmLevel.HIGHEST,
          data: alarmLevelStatistics?.topLevel,
          color: AlarmLevelColorDisplay[AlarmLevel.HIGHEST],
        },
        {
          name: '高级',
          level: AlarmLevel.HIGH,
          data: alarmLevelStatistics?.highLevel,
          color: AlarmLevelColorDisplay[AlarmLevel.HIGH],
        },
        {
          name: '中级',
          level: AlarmLevel.MIDDLE,
          data: alarmLevelStatistics?.middleLevel,
          color: AlarmLevelColorDisplay[AlarmLevel.MIDDLE],
        },
        {
          name: '低级',
          level: AlarmLevel.LOW,
          data: alarmLevelStatistics?.lowLevel,
          color: AlarmLevelColorDisplay[AlarmLevel.LOW],
        },
        {
          name: '最低级',
          level: AlarmLevel.LOWEST,
          data: alarmLevelStatistics?.lowestLevel,
          color: AlarmLevelColorDisplay[AlarmLevel.LOWEST],
        },
      ].filter(item => (item?.data?.total || 0) > 0)
    );
  }, [alarmLevelStatistics]);

  const chartPieOption = useMemo(() => {
    return getPieChartOption(alarmLevelStatistics?.total!, pieLegendData);
  }, [alarmLevelStatistics, pieLegendData]);

  return (
    <SubTitle title="报警统计" img={titlePng}>
      <Spin spinning={loading}>
        <div className={styles.pie_wrapper}>
          <div className={styles.chart_wrapper}>
            <EchartsWithTheme
              ref={chartRef}
              empty={{
                isShowEmpty: !alarmLevelStatistics,
                emptyNode: <Empty />,
              }}
              theme={theme}
              type="pie"
              style={{ height: 120, width: 120 }}
              option={chartPieOption}
            />
          </div>
          <div className={styles.customer_legend}>
            {(pieLegendData ?? []).map(item => (
              <div className={styles.legend_item} key={item.name}>
                <div className={styles.icon} style={{ backgroundColor: item.color }}></div>
                <Marquee>{item.name ?? '-'}</Marquee>
                <div className={styles.value}>{item.data?.total || '-'}</div>
                <div className={styles.proportion}>{keepOneOrLine(Number(item.data?.proportion))}%</div>
              </div>
            ))}
          </div>
        </div>
      </Spin>
    </SubTitle>
  );
};

export default AlarmStatistics;
